class InteractiveProductCard {
    constructor(productCard) {
        this.productCard = productCard;
        this.selectedVariant = null;
        this.productData = null;
        this.productId = productCard.getAttribute('data-product-id');
        this.resizeObserver = null;
        this.init();
    }

    init() {
        this.isMobile = this.detectMobile();
        this.restoreFullSizeLabels();
        this.setupEventListeners();
        this.loadProductData();
        this.checkSizingPillsOverflow();
    }

    restoreFullSizeLabels() {
        this.productCard.querySelectorAll('.clickable-sizes .sizes-list li[data-original-size]').forEach((item) => {
            const originalSize = item.getAttribute('data-original-size');
            if (!originalSize) return;

            const unavailable = item.querySelector('s.unavailable');
            const displayed = (unavailable ? unavailable.textContent : item.textContent).trim();

            if (displayed.toUpperCase() !== originalSize.trim().toUpperCase()) {
                const label = originalSize.trim().toUpperCase();
                if (unavailable) {
                    unavailable.textContent = label;
                } else {
                    item.textContent = label;
                }
            }
        });
    }

    checkSizingPillsOverflow() {
        // Check if sizing pills overflow to second row
        const sizesList = this.productCard.querySelector('.clickable-sizes .sizes-list');
        const clickableSizes = this.productCard.querySelector('.clickable-sizes');
        
        if (sizesList && clickableSizes) {
            // Clean up existing observer if any
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            
            // Use ResizeObserver to detect when items wrap to second row
            this.resizeObserver = new ResizeObserver(() => {
                this.detectOverflow(sizesList, clickableSizes);
            });
            
            this.resizeObserver.observe(sizesList);
            
            // Initial check
            setTimeout(() => {
                this.detectOverflow(sizesList, clickableSizes);
            }, 100);
        }
    }

    detectOverflow(sizesList, clickableSizes) {
        if (!sizesList || !clickableSizes) return;
        
        // Get the first item's height
        const firstItem = sizesList.querySelector('li:first-child');
        if (!firstItem) return;
        
        const firstItemHeight = firstItem.offsetHeight;
        const computedStyle = getComputedStyle(sizesList);
        
        // Parse gap - can be "4px 4px" (row column) or just "4px"
        const gapString = computedStyle.gap || computedStyle.rowGap || '4px';
        const gap = parseFloat(gapString.split(' ')[0]) || 4;
        
        const singleRowHeight = firstItemHeight + gap;
        
        // Check if the list height exceeds single row height (with small tolerance)
        const listHeight = sizesList.scrollHeight;
        const needsSecondRow = listHeight > singleRowHeight + 2; // 2px tolerance
        
        if (needsSecondRow) {
            clickableSizes.classList.add('has-overflow');
        } else {
            clickableSizes.classList.remove('has-overflow');
        }
    }

    detectMobile() {
        // Detect if this is a touch device or mobile screen size
        const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
        const isMobileScreen = window.innerWidth <= 768;
        const isMobile = isTouchDevice || isMobileScreen;
        
        console.log('Mobile detection:', { isTouchDevice, isMobileScreen, isMobile, screenWidth: window.innerWidth });
        return isMobile;
    }

    setupEventListeners() {
        // Handle size clicks
        const sizeItems = this.productCard.querySelectorAll('.clickable-sizes .sizes-list li:not(s)');
        sizeItems.forEach(item => {
            if (!item.querySelector('s')) { // Only add listeners to available sizes
                // Use touchend on mobile for better responsiveness
                if (this.isMobile) {
                    item.addEventListener('touchend', (e) => {
                        e.preventDefault();
                        this.handleSizeClick(e);
                    }, { passive: false });
                } else {
                    item.addEventListener('click', (e) => this.handleSizeClick(e));
                }
            }
        });

        // Handle add to cart button
        const addToCartBtn = this.productCard.querySelector('.quick-add-btn');
        if (addToCartBtn) {
            if (this.isMobile) {
                addToCartBtn.addEventListener('touchend', (e) => {
                    e.preventDefault();
                    this.handleAddToCart(e);
                }, { passive: false });
            } else {
                addToCartBtn.addEventListener('click', (e) => this.handleAddToCart(e));
            }
        }

        // For mobile, add event listeners to show/hide sizing pills
        if (this.isMobile) {
            this.setupMobileInteractions();
        }
    }

    setupMobileInteractions() {
        // Add tap listener to the product card to show sizing pills
        const interactiveVariants = this.productCard.querySelector('.interactive-variants');
        if (interactiveVariants) {
            this.productCard.addEventListener('touchstart', (e) => {
                // Only activate if touching the sizing area or title area
                const target = e.target;
                const isInSizingArea = target.closest('.interactive-variants') || target.closest('.title-cell') || target.closest('.title-price-row');
                
                if (isInSizingArea) {
                    this.activateMobileSizing();
                }
            });

            // Hide sizing pills when touching outside
            document.addEventListener('touchstart', (e) => {
                if (!e.target.closest('product-card')) {
                    this.deactivateMobileSizing();
                }
            });
        }
    }

    handleMobileSizingStart(event) {
        event.preventDefault();
        this.activateMobileSizing();
    }

    activateMobileSizing() {
        if (this.isMobile) {
            console.log('Activating mobile sizing for product:', this.productId);
            this.productCard.classList.add('mobile-sizing-active');
        }
    }

    deactivateMobileSizing() {
        if (this.isMobile) {
            console.log('Deactivating mobile sizing for product:', this.productId);
            this.productCard.classList.remove('mobile-sizing-active');
        }
    }

    async loadProductData() {
        const handle = this.getProductHandle();
        if (!handle) return;

        try {
            const response = await fetch(`/products/${handle}.js`);
            this.productData = await response.json();
        } catch (error) {
            console.error('Failed to load product data:', error);
        }
    }

    getProductHandle() {
        // First try to get from data attribute
        const handle = this.productCard.getAttribute('data-product-handle');
        if (handle) return handle;

        // Fallback: extract from product URL in the card
        const productLink = this.productCard.querySelector('.title a');
        if (productLink) {
            const url = productLink.getAttribute('href');
            return url.split('/products/')[1]?.split('?')[0];
        }
        return null;
    }

    handleSizeClick(event) {
        event.preventDefault();
        event.stopPropagation();
        
        const clickedSize = event.currentTarget;
        const displayedSize = clickedSize.textContent.trim();
        // Get the original size from data attribute for variant matching
        const originalSize = clickedSize.getAttribute('data-original-size') || displayedSize;
        
        console.log('Size clicked:', displayedSize, 'Original:', originalSize); // Debug log
        
        // Remove selected class from all sizes in this card
        this.productCard.querySelectorAll('.clickable-sizes .sizes-list li').forEach(item => {
            item.classList.remove('selected');
        });

        // Add selected class to clicked size
        clickedSize.classList.add('selected');

        // Find the matching variant using the original size name
        if (this.productData) {
            this.selectedVariant = this.productData.variants.find(variant => {
                return variant.options.some(option => 
                    option.toLowerCase().trim() === originalSize.toLowerCase().trim() ||
                    option.toLowerCase().includes(originalSize.toLowerCase())
                );
            });
            console.log('Selected variant:', this.selectedVariant); // Debug log
        }

        // Show add to cart button
        this.showAddToCartButton();
    }

    showAddToCartButton() {
        const addToCartContainer = this.productCard.querySelector('.add-to-cart-inline');
        if (addToCartContainer) {
            addToCartContainer.style.display = 'flex';
        }
    }

    hideAddToCartButton() {
        const addToCartContainer = this.productCard.querySelector('.add-to-cart-inline');
        if (addToCartContainer) {
            addToCartContainer.style.display = 'none';
        }
        
        // Also deactivate mobile sizing when hiding add to cart
        this.deactivateMobileSizing();
    }

    async handleAddToCart(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.selectedVariant) {
            console.error('No variant selected');
            return;
        }

        const button = event.currentTarget;
        const originalText = button.textContent;
        
        // Show loading state
        button.textContent = 'Adding...';
        button.disabled = true;

        try {
            const shouldRedirectToCart = window.themeCartType === 'redirect';

            if (window.liquidAjaxCart && window.liquidAjaxCart.add) {
                window.liquidAjaxCart.add({
                    items: [{
                        id: this.selectedVariant.id,
                        quantity: 1
                    }]
                });

                if (!shouldRedirectToCart) {
                    button.textContent = 'Added!';
                    setTimeout(() => {
                        button.textContent = originalText;
                        button.disabled = false;
                        this.hideAddToCartButton();
                        this.productCard.querySelectorAll('.clickable-sizes .sizes-list li').forEach(item => {
                            item.classList.remove('selected');
                        });
                        this.deactivateMobileSizing();
                    }, 2000);
                }

                return;
            }

            const formData = {
                'items': [{
                    'id': this.selectedVariant.id,
                    'quantity': 1
                }]
            };

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const addResult = await response.json();

            if (!response.ok) {
                throw new Error(addResult?.description || 'Failed to add to cart');
            }

            button.textContent = 'Added!';
            
            const cartResponse = await fetch('/cart.js');
            if (cartResponse.ok) {
                const cart = await cartResponse.json();
                
                const itemCount = cart.item_count;
                document.querySelectorAll('cart-count[data-count]').forEach(function(el) {
                    el.setAttribute('data-count', itemCount);
                });
                document.querySelectorAll('cart-count .counter').forEach(function(el) {
                    el.textContent = itemCount;
                });
                const cartButton = document.getElementById('cart-icon-bubble');
                if (cartButton) {
                    cartButton.setAttribute('data-cart-count', itemCount);
                }
            }
            
            document.dispatchEvent(new CustomEvent('cart:updated'));

            if (shouldRedirectToCart) {
                if (window.themeRedirectToCart) {
                    window.themeRedirectToCart();
                } else {
                    window.location.href = (window.routes && window.routes.cart_url) || '/cart';
                }
                return;
            }

            setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
                this.hideAddToCartButton();
                this.productCard.querySelectorAll('.clickable-sizes .sizes-list li').forEach(item => {
                    item.classList.remove('selected');
                });
                this.deactivateMobileSizing();
            }, 2000);
        } catch (error) {
            console.error('Error adding to cart:', error);
            button.textContent = 'Error';
            setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
                this.hideAddToCartButton();
                this.productCard.querySelectorAll('.clickable-sizes .sizes-list li').forEach(item => {
                    item.classList.remove('selected');
                });
                // Reset mobile sizing state
                this.deactivateMobileSizing();
            }, 2000);
        }
    }
}

// Initialize interactive product cards
function initializeInteractiveCards() {
    // Find all product cards with interactive variants
    const productCards = document.querySelectorAll('product-card[data-product-id]');
    
    productCards.forEach(card => {
        // Skip if already initialized
        if (card.hasAttribute('data-interactive-initialized')) {
            // Re-check overflow for existing cards (in case of resize or content change)
            const existingCard = card._interactiveCard;
            if (existingCard && existingCard.checkSizingPillsOverflow) {
                existingCard.checkSizingPillsOverflow();
            }
            return;
        }
        
        // Mark as initialized
        card.setAttribute('data-interactive-initialized', 'true');
        
        // Only initialize if it has interactive variants
        if (card.querySelector('.interactive-variants')) {
            console.log('Initializing interactive card for product:', card.getAttribute('data-product-id')); // Debug log
            
            // Create the interactive card instance
            const interactiveCard = new InteractiveProductCard(card);
            
            // Store reference on the card element for future access
            card._interactiveCard = interactiveCard;
        }
    });
}

window.initializeInteractiveCards = initializeInteractiveCards;

let interactiveCardsInitTimer;
const scheduleInteractiveCardsInit = () => {
    clearTimeout(interactiveCardsInitTimer);
    interactiveCardsInitTimer = setTimeout(initializeInteractiveCards, 50);
};

const observeNewProductCards = () => {
    if (!document.body) return;

    const cardObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (
                    node.matches?.('product-card[data-product-id]') ||
                    node.querySelector?.('product-card[data-product-id]')
                ) {
                    scheduleInteractiveCardsInit();
                    return;
                }
            }
        }
    });

    cardObserver.observe(document.body, { childList: true, subtree: true });
};

document.addEventListener('DOMContentLoaded', () => {
    observeNewProductCards();
    initializeInteractiveCards();
});

document.addEventListener('shopify:section:load', initializeInteractiveCards);
document.addEventListener('shopify:section:reload', initializeInteractiveCards);
setTimeout(initializeInteractiveCards, 2000);
